import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'lodash';
import clsx from 'clsx';
import { currentMonth, monthForward } from 'helpers/dates';

import { useControlled } from '@mui/material/utils';
import s from './styled';

const KeyboardDateRangePicker = memo(
  forwardRef(function KeyboardDateRangePicker(props, ref) {
    const {
      className,
      value,
      default: defaultValue = [currentMonth, monthForward],
      singleView,
      labelStart,
      labelEnd,
      placeholder,
      format = 'MM/dd/yyyy',
      onChange,
      onBlur,
      KeyboardDatePickerProps: KeyboardDatePickerPropsProp,
      DateRangePickerProps: DateRangePickerPropsProp,
      errors,
    } = props;

    const [selectedRange, setSelectedRange] = useControlled({
      controlled: value,
      default: defaultValue,
      name: 'KeyboardDateRangePicker',
      state: 'range',
    });

    const onChangeRange = useCallback(
      (range) => {
        setSelectedRange(range);
        onChange?.(range, 'range');
        onBlur?.();
      },
      [setSelectedRange, onChange, onBlur],
    );

    const onChangeRangeKeyboardStart = useCallback(
      (date) => {
        const newRange = [date, selectedRange[1]];
        setSelectedRange(newRange);
        onChange?.(newRange, 'keyboard-start');
      },
      [selectedRange, setSelectedRange, onChange],
    );

    const onChangeRangeKeyboardEnd = useCallback(
      (date) => {
        const newRange = [selectedRange[0], date];
        setSelectedRange(newRange);
        onChange?.(newRange, 'keyboard-end');
      },
      [selectedRange, setSelectedRange, onChange],
    );

    const KeyboardDatePickerProps = useMemo(() => {
      if (isFunction(KeyboardDatePickerPropsProp)) {
        const startProps = KeyboardDatePickerPropsProp('start');
        const endProps = KeyboardDatePickerPropsProp('end');

        return {
          startProps,
          endProps,
        };
      }

      return {
        startProps: KeyboardDatePickerPropsProp,
        endProps: KeyboardDatePickerPropsProp,
      };
    }, [KeyboardDatePickerPropsProp]);

    return (
      <s.DatePickerWrapper
        className={clsx(className, 'KeyboardDateRangePicker-root')}
        ref={ref}
      >
        <s.DatePickerInputs>
          <s.KeyboardDatePicker
            fullWidth
            maxDate={selectedRange[1]}
            value={selectedRange[0]}
            onChange={onChangeRangeKeyboardStart}
            onBlur={onBlur}
            label={labelStart}
            placeholder={placeholder}
            format={format}
            className={errors && errors[0] ? 'field-error' : ''}
            {...KeyboardDatePickerProps?.startProps}
          />
          <s.DatePickerDivider>TO</s.DatePickerDivider>
          <s.KeyboardDatePicker
            fullWidth
            minDate={selectedRange[0]}
            value={selectedRange[1]}
            onChange={onChangeRangeKeyboardEnd}
            onBlur={onBlur}
            label={labelEnd}
            placeholder={placeholder}
            format={format}
            className={errors && errors[1] ? 'field-error' : ''}
            {...KeyboardDatePickerProps?.endProps}
          />
        </s.DatePickerInputs>

        <s.DateRangePicker
          value={selectedRange}
          onChange={onChangeRange}
          singleView={singleView}
          {...DateRangePickerPropsProp}
        />
      </s.DatePickerWrapper>
    );
  }),
);

KeyboardDateRangePicker.propTypes = {
  className: PropTypes.string,
  value: PropTypes.array,
  default: PropTypes.array,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,

  singleView: PropTypes.bool,
  labelStart: PropTypes.node,
  labelEnd: PropTypes.node,
  placeholder: PropTypes.string,
  format: PropTypes.string,
  KeyboardDatePickerProps: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
  ]),
  DateRangePickerProps: PropTypes.object,
  errors: PropTypes.array,
};

export default KeyboardDateRangePicker;
